import axios from 'axios';
import { formatDisplayDate } from '../../lib/dateDisplay';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { connect, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { AuthAction } from '../../actions/AuthAction';
import { ProductAction } from '../../actions/ProductAction';
import { ProgramConstant } from '../../actions/constants/ProgramConstant';
import { AdminProductListHeader, RayTable, RayTreeview } from '../../components/common';
import { Translate as t } from '../../config/Translate';
import { ORG_HQ, ORG_BRANCH } from '../../config/appConstants';
import { pmcfDesignatePayload, pmcfFilterMatch } from '../../lib/pmcfHelper';

// ── PMCF 정보 모달 ─────────────────────────────────────────────────────────────
// FEAT-037 Open Q2: pmcf-api 는 사용자 제공 계약 기준 공개 GET(인증키 없음) 가정.
//   인증키 필요로 확정되면 키 FE 노출 방지 위해 BE(Lambda) 프록시 경유로 전환 필요.
//   CORS allowlist(FE Origin 허용 여부)는 BE 확인 대상.
const PMCF_API_BASE = 'https://pmcf-api.rayplatform.com';

// collected/discard 는 epoch timestamp. 표본상 collected=ms(13자리), discard=초(10자리)로 단위 혼재 가능
// → 1e12 미만이면 초로 간주하여 ms 정규화(1970 오표시 방지). 경계 1e12ms = 2001-09-09 이므로
//   2001년 이전 ms 값은 초로 오판 가능(PMCF 장비 데이터는 그 이후 전제). 단위 확정은 BE 확인 대상.
const toMs = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? (v < 1e12 ? v * 1000 : v) : null);
const fmtDate = (v) => { const ms = toMs(v); return ms ? formatDisplayDate(ms) : '-'; };

export const ModalPmcfInfo = ({ showModal, device, callbackClose }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cases, setCases] = useState([]);

    useEffect(() => {
        if (!showModal || !device?.sn) return;

        let cancelled = false;

        setLoading(true);
        setError(null);
        setCases([]);

        axios.get(`${PMCF_API_BASE}/api/v1/devices/${encodeURIComponent(device.sn)}/dates`)
            .then((res) => {
                if (cancelled) return;
                setCases(Array.isArray(res?.data?.cases) ? res.data.cases : []);
            })
            .catch((e) => {
                if (cancelled) return;
                console.warn('[PMCF] dates fetch failed', { sn: device?.sn, reason: e?.message });
                setError(e?.message || 'FETCH_FAILED');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [showModal, device?.sn]);

    return (
        <Modal show={showModal} onHide={callbackClose} className="action-sheet" centered>
            <Modal.Header>
                <h2 className="modal-title">{t('pmcf.title')}{device?.sn ? ` — ${device.sn}` : ''}</h2>
            </Modal.Header>
            <Modal.Body className="p-3">
                {loading && (
                    <div>{t('common.status.loading')}</div>
                )}
                {/* 조회 실패(error)도 사용자 관점에서는 '표시할 데이터 없음'으로 통일.
                    실패 원인은 catch 의 console.warn 으로 관측 (silent-drop 아님). */}
                {!loading && (error || cases.length === 0) && (
                    <div>{t('common.message.noData')}</div>
                )}
                {!loading && !error && cases.length > 0 && (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>{t('pmcf.field.collectedDate')}</th>
                                <th>{t('pmcf.field.discardDate')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cases.map((c, idx) => (
                                // 외부 API 응답의 id 미보장(Open Q2) → 없으면 인덱스 fallback 으로 key 중복/undefined 방지
                                <tr key={c?.id != null ? c.id : `pmcf-case-${idx}`}>
                                    <td>{fmtDate(c.collected)}</td>
                                    <td>{fmtDate(c.discard)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-block btn-dark" onClick={callbackClose}>{t('common.button.close')}</button>
            </Modal.Footer>
        </Modal>
    );
};

// ── 해제 확인 모달 ──────────────────────────────────────────────────────────────
const ModalUndesignateConfirm = ({ showModal, callbackConfirm, callbackClose }) => {
    return (
        <Modal show={showModal} onHide={callbackClose} className="action-sheet">
            <Modal.Header>
                <h2 className="modal-title">{t('pmcf.button.undesignate')}</h2>
            </Modal.Header>
            <Modal.Body className="p-0">
                <ul className="listview simple-listview no-space border-0 m-2">
                    <li className="p-2">{t('pmcf.message.confirmOff')}</li>
                </ul>
            </Modal.Body>
            <Modal.Footer>
                <button
                    className="btn btn-block btn-primary mb-0"
                    onClick={callbackConfirm}
                >
                    {t('common.button.confirm')}
                </button>
                <button className="btn btn-block btn-dark" onClick={callbackClose}>
                    {t('common.button.cancel')}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

// ── 메인 화면 ──────────────────────────────────────────────────────────────────
export const AdminPMCFRegistry = ({
    user,
    viewTree,
    products,
    totalproductcount,
    filterOptions,
    sort,
    page,
    GetProductAllV2,
    BulkUpdateProductInfo,
    Logout,
}) => {
    const dispatch = useDispatch();

    // ── Hooks (rules-of-hooks: 조건 반환 앞에 위치) ────────────────────────────
    const [header, setHeader] = useState([]);
    const [ready, setReady] = useState(false);
    const [bulkList, setBulkList] = useState([]);
    const [match, setMatch] = useState({});
    const [filterCompanyId, setFilterCompanyId] = useState({});
    const [pmcfOnly, setPmcfOnly] = useState(false);
    const [showOffConfirm, setShowOffConfirm] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [infoDevice, setInfoDevice] = useState(null);
    const [wHg, setwHg] = useState(300);

    const canManagePmcf = user?.adminType === 'S' && (user?.companyInfo?.org === ORG_HQ || user?.companyInfo?.org === ORG_BRANCH);

    useEffect(() => {
        if (canManagePmcf) {
            dispatch({ type: ProgramConstant.VIEWTREE, val: true });
        }
        setHeader([
            { label: t('pmcf.field.active'), name: 'isPmcfDevice', type: 'CALLBACK_ICON', insortable: true, options: { paramType: 'callback', title: t('pmcf.field.active') } },
            ...AdminProductListHeader,
        ]);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setReady(header.length > 0);
    }, [header]);

    // ── PMCF 관리 권한 가드 (adminType S + HQ 또는 Branch org) ─────────────────
    if (!canManagePmcf) return <Redirect to="/Home" />;

    // ── 핸들러 ─────────────────────────────────────────────────────────────────
    const handleFilterToggle = () => {
        const next = !pmcfOnly;
        setPmcfOnly(next);
        setMatch(next ? pmcfFilterMatch() : {});
    };

    const bulkActions = [
        { title: t('pmcf.button.designate'), action: 'PMCF_ON', className: 'btn btn-success mt-0' },
        { title: t('pmcf.button.undesignate'), action: 'PMCF_OFF', className: 'btn btn-danger mt-0' },
    ];

    const bulkActionDo = (action) => {
        if (!bulkList || bulkList.length === 0) return;
        if (action === 'PMCF_ON') {
            BulkUpdateProductInfo(bulkList, pmcfDesignatePayload(true));
            setBulkList([]);
        } else if (action === 'PMCF_OFF') {
            setShowOffConfirm(true);
        }
    };

    const handleUndesignateConfirm = () => {
        if (bulkList && bulkList.length > 0) {
            BulkUpdateProductInfo(bulkList, pmcfDesignatePayload(false));
            setBulkList([]);
        }
        setShowOffConfirm(false);
    };

    const filterpage = {
        filters: {},
        filterOptions: ['model'],
        searchCols: ['model', 'option', 'sn', 'site.sitename'],
        sort: sort || { updateDate: -1 },
        searchKey: '',
        match,
        page: page || 1,
        itemperpage: 30,
    };

    const options = {
        isShowCheckBox: true,
        search: true,
        keyCol: 'sn',
        linkto: '/Admin/AdminProductInfo',
        linkkey: 'sn',
        getList: GetProductAllV2,
        filterpageoption: filterpage,
        itemperpage: 30,
        useapi: true,
        totalitemcount: totalproductcount,
        filterOptions,
    };

    return (
        <>
            <div className="extraHeader justify-content-between">
                <h2 className="mb-0">{t('pmcf.title')}</h2>
                <button
                    className="btn btn-secondary mt-0"
                    type="button"
                    onClick={() => Logout()}
                >
                    {/* IMP-289: 사이드 메뉴(AdminLeftMenu) sign out 과 동일 아이콘(log-out-outline). */}
                    <ion-icon name="log-out-outline" style={{ marginRight: '4px', verticalAlign: 'middle' }}></ion-icon>
                    {t('auth.button.signOut')}
                </button>
            </div>
            <div id="appCapsule" className="extra-header-active full-height">
                {(viewTree && user?.adminType !== 'U') && (
                    <div className="section fl p-0" style={{ width: wHg }}>
                        <div className="p-1">
                            <RayTreeview
                                onClickItem={(item) => setFilterCompanyId({ companyId: item.id })}
                                onResize={setwHg}
                            />
                        </div>
                    </div>
                )}
                <div className="section m-1 p-0">
                    <div className="card p-2">
                        <div className={user?.adminType !== 'U' ? 'fl' : ''}>
                            {ready && (
                                <RayTable
                                    data={products}
                                    listtype="product"
                                    columns={header}
                                    addFilter={filterCompanyId}
                                    match={match}
                                    bulkActions={bulkActions}
                                    bulkActionDo={bulkActionDo}
                                    checkBoxCallback={(list) => setBulkList(list)}
                                    calllback={(item) => { setInfoDevice(item); setShowInfoModal(true); }}
                                    options={options}
                                    headerSlotRight={
                                        <button
                                            className={`btn mt-0 ${pmcfOnly ? 'btn-primary' : 'btn-secondary'}`}
                                            onClick={handleFilterToggle}
                                        >
                                            {t('pmcf.button.filterCollect')}
                                        </button>
                                    }
                                />
                            )}
                        </div>
                        <div className="clearfix"></div>
                    </div>
                </div>
            </div>
            <ModalUndesignateConfirm
                showModal={showOffConfirm}
                callbackConfirm={handleUndesignateConfirm}
                callbackClose={() => setShowOffConfirm(false)}
            />
            <ModalPmcfInfo showModal={showInfoModal} device={infoDevice} callbackClose={() => setShowInfoModal(false)} />
        </>
    );
};

const mapState = (state) => ({
    user: state.AuthReducer.user,
    viewTree: state.ProgramReducer.viewtree,
    products: state.ProductReducer.products,
    totalproductcount: state.ProductReducer.totalproductcount,
    filterOptions: state.ProductReducer.filterOptions,
    sort: state.ProductReducer.sort,
    page: state.ProductReducer.page,
});

const mapDispatch = (dispatch) => ({
    GetProductAllV2: (filter) => dispatch(ProductAction.GetProductAllV2(filter)),
    BulkUpdateProductInfo: (sns, data) => dispatch(ProductAction.BulkUpdateProductInfo(sns, data)),
    Logout: () => dispatch(AuthAction.Logout()),
});

export default connect(mapState, mapDispatch)(AdminPMCFRegistry);
